import { definePreset } from '@primeuix/themes';
import { colorScheme as laraButton } from '@primeuix/themes/lara/button';
import { colorScheme as laraTag } from '@primeuix/themes/lara/tag';
import { PurpleLara } from '@dgacontrol-frontend/sakai-ng';

import { THEME_INFO, THEME_PRIMARY, type ThemePalette } from './theme.config';

const SHADES = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const;
type Shade = (typeof SHADES)[number];

/** The 11 shades of a palette as its primitive tokens: `{cyan.50}` … `{cyan.950}`. */
export function primaryPalette(name: ThemePalette): Record<Shade, string> {
  return Object.fromEntries(SHADES.map((shade) => [shade, `{${name}.${shade}}`])) as Record<Shade, string>;
}

/**
 * Lara's `primary` button (filled, outlined, text) of one color scheme, reused as its `success`
 * one: «Guardar» and the header «+» follow the theme primary instead of Lara's fixed green.
 * `.text-success` and toasts keep the green: there it means «ok / yes».
 */
function successAsPrimary(mode: 'light' | 'dark') {
  const lara = laraButton[mode];
  return {
    root: { success: lara?.root?.primary },
    outlined: { success: lara?.outlined?.primary },
    text: { success: lara?.text?.primary },
  };
}

/**
 * Lara's secondary tag is a faint pill (surface.800 on the surface.900 row in dark, surface.100
 * on white in light): the same recipe as the colored tags instead, in the slate surface.
 */
const SECONDARY_TAG = {
  light: { background: '{surface.500}', color: '{surface.0}' },
  dark: { background: '{surface.400}', color: '{surface.950}' },
};

/**
 * Dark warn and danger tags in neon, off the palette (picked by the user on the Task 6 mockup of
 * dt-table-style-uniform): lime and cyan glow from their lightness (OKLCH L 0.85 / 0.80), which a
 * saturated red can't reach, so red goes to the sRGB max chroma (#ff0000, C 0.258). Danger text
 * is surface.950: red.950 on #ff0000 is 4.04:1, below WCAG AA (5.05:1); orange.950 on #ff6a00 is
 * 5.45:1. Light already uses the 500s.
 */
const DARK_ALERT_TAGS = {
  warn: { background: '#ff6a00', color: '{orange.950}' },
  danger: { background: '#ff0000', color: '{surface.950}' },
};

/**
 * Lara's status tags of one color scheme, retuned: `success` («Convertida», «Atendida») is the
 * `primary` tag, `info` («Vigente») takes the `THEME_INFO` palette instead of `sky`,
 * `secondary` (the conversion-removal blockers) is `SECONDARY_TAG`, and dark `warn`/`danger`
 * are `DARK_ALERT_TAGS`.
 */
function statusTags(mode: 'light' | 'dark') {
  const lara = laraTag[mode];
  const toInfo = (token?: string) => token?.replace('{sky.', `{${THEME_INFO}.`);
  return {
    success: lara?.primary,
    info: { background: toInfo(lara?.info?.background), color: toInfo(lara?.info?.color) },
    secondary: SECONDARY_TAG[mode],
    ...(mode === 'dark' ? DARK_ALERT_TAGS : {}),
  };
}

/** The loading spinner (`p-progressSpinner`) in the primary only, instead of Lara's 4-color cycle. */
const spinnerInPrimary = {
  colorOne: '{primary.color}',
  colorTwo: '{primary.color}',
  colorThree: '{primary.color}',
  colorFour: '{primary.color}',
};

/** PurpleLara (slate dark surfaces, button gap) with the primary from `theme.config.ts`. */
export const AppThemePreset = definePreset(PurpleLara, {
  semantic: { primary: primaryPalette(THEME_PRIMARY) },
  components: {
    button: {
      colorScheme: { light: successAsPrimary('light'), dark: successAsPrimary('dark') },
    },
    progressspinner: {
      colorScheme: { light: { root: spinnerInPrimary }, dark: { root: spinnerInPrimary } },
    },
    tag: {
      colorScheme: { light: statusTags('light'), dark: statusTags('dark') },
    },
    datatable: {
      // Cell lines one shade up in dark mode (Lara: surface.800, too close to the row background).
      colorScheme: { dark: { root: { borderColor: '{surface.700}' } } },
      // p-table renders the <tfoot> whenever a `footer` template exists, even with no row in
      // it (the «Cargando más…» row is off between pages), and with gridlines PrimeNG drops the
      // last body row's bottom border when there is a <tfoot>: give it back while the footer
      // has no cell. Specificity (0,7,3) beats PrimeNG's (0,7,2) td:last-child rule. Neither
      // Lara nor PurpleLara define a datatable `css`, so nothing is overridden.
      css: () => `
        .p-datatable.p-datatable-gridlines:has(.p-datatable-tbody):has(.p-datatable-tfoot):not(:has(.p-datatable-tfoot td))
          .p-datatable-tbody > tr:last-child > td {
          border-bottom-width: 1px;
        }
      `,
    },
    select: {
      // A select still loading its options (SelectLoadingDirective): primeicons' spinner before
      // «Cargando…». Glyph and keyframes are primeicons.css's `.pi-spinner` / `.pi-spin`.
      // With virtual scroll only ~12 rows exist, so the panel can't grow to the widest option
      // like a plain list does: give it room (25rem = 325 px at the 13 px root; the widest
      // local user is 323 px, 2026-10-27) and cut what still doesn't fit with «…» instead of a
      // sideways scrollbar. The scroller's <ul> is absolute with min-width 100% and grows to its
      // widest row unless pinned to the panel width.
      css: () => `
        .p-select-overlay.lib-select-loading .p-select-empty-message::before {
          content: '\\e926';
          font-family: 'primeicons';
          display: inline-block;
          margin-inline-end: 0.5rem;
          animation: fa-spin 2s infinite linear;
        }
        .p-select-overlay:has(.p-virtualscroller) {
          min-width: max(100%, 25rem);
        }
        .p-select-overlay .p-virtualscroller {
          overflow-x: hidden;
        }
        .p-select-overlay .p-virtualscroller-content {
          width: 100%;
        }
        .p-select-overlay .p-virtualscroller .p-select-option > span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `,
    },
  },
});
